import React from 'react';
import { Card, Tag, Typography, Empty } from 'antd';
import { Link } from 'react-router-dom';
import {
    AI_HUB_SUBSYSTEMS,
    SUBSYSTEM_GROUP_LABELS,
    SUBSYSTEM_GROUP_ORDER,
} from '../ai-hub-routes';
import { SubsystemSummary, SubsystemId } from '../../../services/ai-admin-api';

const { Text } = Typography;

interface Props {
    subsystems: SubsystemSummary[];
    loading?: boolean;
}

/**
 * Hand-rolled grid layout grouping all 11 subsystems by their Q12 IA category.
 * The single inter-AI edge (Advice → Orbital) is rendered as an arrow tag on
 * the Orbital node. A React-Flow upgrade can wait for Phase 6 polish.
 */
export const RelationshipDiagram: React.FC<Props> = ({ subsystems, loading }) => {
    const byId = new Map<string, SubsystemSummary>();
    for (const s of subsystems) byId.set(s.id, s);

    const grouped: Record<string, typeof AI_HUB_SUBSYSTEMS> = {};
    for (const s of AI_HUB_SUBSYSTEMS) {
        grouped[s.group] = grouped[s.group] ?? [];
        grouped[s.group].push(s);
    }

    if (!loading && subsystems.length === 0) {
        return (
            <Empty description="No subsystem state loaded yet — check that the v1 baseline has been seeded under /admin/tools." />
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {SUBSYSTEM_GROUP_ORDER.map((group) => {
                const entries = grouped[group];
                if (!entries?.length) return null;
                return (
                    <div key={group}>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            {SUBSYSTEM_GROUP_LABELS[group]}
                        </Text>
                        <div
                            style={{
                                display: 'grid',
                                gap: 16,
                                gridTemplateColumns:
                                    'repeat(auto-fit, minmax(220px, 1fr))',
                            }}
                        >
                            {entries.map((entry) => {
                                const live = byId.get(entry.id);
                                return (
                                    <Link
                                        key={entry.id}
                                        to={`/admin/ai/${entry.id}`}
                                        style={{ display: 'block' }}
                                    >
                                        <Card
                                            hoverable
                                            size="small"
                                            style={{
                                                borderColor: live?.hasDraft
                                                    ? '#fa8c16'
                                                    : undefined,
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                                                <Text strong>{entry.displayName}</Text>
                                                <Tag>
                                                    v{live?.currentPublishedVersion ?? '?'}
                                                </Tag>
                                            </div>
                                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                                                {entry.subtitle}
                                            </Text>
                                            <div style={{ marginTop: 8 }}>
                                                {live?.hasDraft && (
                                                    <Tag color="orange">Draft pending</Tag>
                                                )}
                                                {entry.dependencies.length > 0 && (
                                                    <Tag color="blue">
                                                        ← consumes{' '}
                                                        {entry.dependencies
                                                            .map(
                                                                (d) =>
                                                                    AI_HUB_SUBSYSTEMS.find(
                                                                        (s) => s.id === (d as SubsystemId),
                                                                    )?.displayName ?? d,
                                                            )
                                                            .join(', ')}
                                                    </Tag>
                                                )}
                                            </div>
                                        </Card>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
